import React, { useState } from 'react';
import { Form, Input, Button, notification, Typography } from 'antd';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { LockOutlined, MailOutlined, UserOutlined, SafetyCertificateOutlined, BarChartOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import adminApi from '../../api/adminAxios';
import SEO from '../../components/SEO';
import '../../admin.css';

const { Title, Text } = Typography;

const features = [
    { icon: <TeamOutlined />, label: 'User & Profile Management' },
    { icon: <SafetyCertificateOutlined />, label: 'Document Verification' },
    { icon: <BarChartOutlined />, label: 'Revenue & Analytics' },
];

export default function AdminLogin() {
    const [loading, setLoading] = useState(false);
    const { login } = useAdminAuth();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleSubmit = async (values) => {
        if (!executeRecaptcha) {
            notification.error({ message: 'reCAPTCHA Not Ready', description: 'Security check is still loading. Please wait a moment and try again.' });
            return;
        }
        setLoading(true);
        try {
            const recaptchaToken = await executeRecaptcha('admin_login');
            const res = await adminApi.post('/admin/login', { ...values, recaptcha_token: recaptchaToken });
            if (res.data.status === 'success') {
                const { mfa_enabled, mfa_token, mfa_type } = res.data.data || {};

                if (mfa_enabled) {
                    navigate('/admin/mfa', { state: { mfa_token, mfa_type }, replace: true });
                    return;
                }

                login(res.data.data.admin);
                notification.success({ message: 'Welcome back!', description: `Logged in as ${res.data.data.admin.name}` });
                navigate('/admin/dashboard', { replace: true });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Invalid credentials.';
            notification.error({ message: 'Login Failed', description: msg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-login-root">
            <SEO noindex={true} />
            {/* Left panel */}
            <div className="admin-login-left">
                <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 320 }}>
                    <div style={{
                        width: 64, height: 64, background: 'rgba(255,255,255,0.15)',
                        borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 24px', fontSize: 28, color: '#FCD34D',
                    }}>
                        <UserOutlined />
                    </div>
                    <Title level={2} style={{ color: '#fff', fontWeight: 800, margin: '0 0 8px' }}>
                        Oottan Admin
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, display: 'block', marginBottom: 36 }}>
                        Manage your matrimony platform from one powerful portal.
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {features.map((f) => (
                            <div key={f.label} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                background: 'rgba(255,255,255,0.1)', borderRadius: 10,
                                padding: '10px 16px', textAlign: 'left',
                            }}>
                                <span style={{ color: '#FCD34D', fontSize: 16 }}>{f.icon}</span>
                                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, fontWeight: 500 }}>
                                    {f.label}
                                </Text>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right panel */}
            <div className="admin-login-right">
                <div className="admin-login-form-box">
                    <div style={{ marginBottom: 32, textAlign: 'center' }}>
                        <Title level={3} style={{ fontWeight: 800, color: 'var(--color-text-primary)', margin: '0 0 6px' }}>
                            Admin Sign In
                        </Title>
                        <Text style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>
                            Enter your admin credentials to continue
                        </Text>
                    </div>

                    <Form layout="vertical" onFinish={handleSubmit} size="large">
                        <Form.Item
                            name="email"
                            label="Email Address"
                            style={{ marginBottom: 6 }}
                            rules={[
                                { required: true, message: 'Email is required' },
                                { type: 'email', message: 'Enter a valid email' },
                            ]}
                        >
                            <Input prefix={<MailOutlined style={{ color: 'var(--color-text-muted)' }} />}
                                placeholder="Enter your email" />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            label="Password"
                            style={{ marginBottom: 6 }}
                            rules={[{ required: true, message: 'Password is required' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: 'var(--color-text-muted)' }} />}
                                placeholder="Enter your password"
                            />
                        </Form.Item>

                        <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                style={{
                                    height: 48, fontWeight: 700, fontSize: 15,
                                    background: 'var(--gradient-primary)',
                                    border: 'none', borderRadius: 'var(--radius-md)',
                                }}
                            >
                                Sign In to Admin Portal
                            </Button>
                        </Form.Item>
                    </Form>

                </div>
            </div>
        </div>
    );
}
